export interface AccountStatementItem {
	/** @format date-time */
	date: string;
	transaction: string;
	debit: string;
	credit: string;
	balance: string;
	viewLink?: string;
	viewBillIdentifier: boolean;
}

export interface AccountStatementResponse {
	items: AccountStatementItem[];
}

export interface AccountStatementResponseOkModel {
	result: AccountStatementResponse;
}

export interface ActivateAccountInput {
	activationCode: string;
	isDigitalBillingPromo?: boolean;
}

export interface AddressResult {
	houseNumber?: string;
	street: string;
	supplementaryLine1?: string;
	supplementaryLine2?: string;
	supplementaryLine3?: string;
	city: string;
	country: string;
	region?: string;
	postcode: string;
}

export interface AnonymousCheckResponse {
	/** @format double */
	accountBalance: number;
	/** @format double */
	amountDue: number;
	/** @format date-time */
	currentDueDate: string;
	paymentMethod: string;
}

export interface AnonymousCheckResponseOkModel {
	result: AnonymousCheckResponse;
}

export interface AnonymousMeterDetailsInput {
	contractAccountNumber: string;
	lastName: string;
	/** @format int64 */
	customerNumber: number;
}

export interface AnonymousMoveOutConfirmationInput {
	paymentReference: string;
	/** @format date-time */
	moveOutDate: string;
	meterDetails?: MoveOutMeterReadingInput;
	forwardingAddress: SubmitAddressAnonymousInput;
	lastName: string;
	/** @format int64 */
	customerNumber: number;
	emailAddress: string;
}

export interface AnonymousMoveOutCustomerDetailsInput {
	paymentReference: string;
	lastName: string;
	/** @format int64 */
	customerNumber: number;
}

export interface AnonymousMoveOutMeter {
	id: string;
	category: string;
	/** @format int32 */
	lastReading: number;
	/** @format date-time */
	lastReadDate: string;
}

export interface AnonymousPaymentCheckDetailsInput {
	contractAccountNumber: string;
	lastName: string;
	/** @format int64 */
	customerNumber: number;
}

export interface AnonymousPaymentInput {
	/** @format double */
	paymentAmount: number;
	contractAccountNumber: string;
	emailId: string;
}

export interface AnonymousPaymentStatusInput {
	/** @format double */
	paymentAmount: number;
	contractAccountNumber: string;
	/** @format int64 */
	customerNumber: number;
}

export interface AuthenticatedCustomer {
	/** @format int64 */
	customerNumber: number;
	firstName: string;
	lastName: string;
	contractAccounts: string[];
	optedForPaperless: string;
	emailAddress: string;
	noAssociatedPropertyFound: boolean;
}

export interface AuthenticatedCustomerOkModel {
	result: AuthenticatedCustomer;
}

export interface AuthenticatedCustomerProfileDetails {
	/** @format int64 */
	customerNumber: number;
	emailAddress: string;
	mobileNumber: string;
	telephoneNumber: string;
	mobileIsdCode: string;
	telephoneIsdCode: string;
	correspondenceAddress: AddressResult;
}

export interface AuthenticatedCustomerProfileDetailsOkModel {
	result: AuthenticatedCustomerProfileDetails;
}

export interface AuthenticatedCustomerPropertiesDetails {
	paymentReference: string;
	/** @deprecated */
	conractAccountId: string;
	contractAccountId: string;
	contractId: string;
	isPropertyActive: boolean;
	/** @format date-time */
	moveInDate: string;
	propertyAddress: AddressResult;
	/** @format date-time */
	moveOutDate?: string;
}

export interface AuthenticatedCustomerPropertiesDetailsListOkModel {
	result: AuthenticatedCustomerPropertiesDetails[];
}

export interface AuthenticatedTokenModel {
	/** @format date-time */
	accessTokenExpiration: string;
}

export interface AuthenticatedTokenModelOkModel {
	result: AuthenticatedTokenModel;
}

export interface BadRequestResultModel {
	type: string;
	title: string;
	/** @format int32 */
	status: number;
	traceId: string;
	errors: any;
}

export interface BankDetails {
	accountHolderName: string;
	bankAccountNo: string;
	sortCode: string;
}

export interface BankDetailsInput {
	accountHolderName: string;
	bankAccountNo: string;
	sortCode: string;
}

export interface ChangePasswordInput {
	oldPassword: string;
	newPassword: string;
}

export interface CloseAccountAnonymousUserDetails {
	contractId: string;
	propertyAddress: AddressResult;
	meters: AnonymousMoveOutMeter[];
	firstName: string;
	lastName: string;
	assessedProperty: string;
	paymentMethod: string;
}

export interface CloseAccountAnonymousUserDetailsOkModel {
	result: CloseAccountAnonymousUserDetails;
}

export interface ConfirmMoveoutAnonymousResponse {
	isMoveoutForcefullySucceeded: boolean;
	/** @format double */
	finalBillAmount?: number;
}

export interface ConfirmMoveoutAnonymousResponseOkModel {
	result: ConfirmMoveoutAnonymousResponse;
}

export interface ConfirmMoveOutInInput {
	/** @format date-time */
	moveInDate: string;
	/** @format date-time */
	moveOutDate: string;
	contractId: string;
	moveOutMeterData?: MeterReadInput;
	noPersons: string;
	occupancyType: string;
	moveOutAddress: CustomerCreateAddressInput;
	moveInAddress: CustomerCreateAddressInput;
	correspondenceAddress?: CustomerCreateAddressInput;
	paymentDetails?: DirectDebitDetailsInput;
	isManualAddress: boolean;
}

export interface ConfirmMoveOutInResponse {
	paymentReferenceNumber: string;
	customerServiceFlag: string;
	directDebitSetupFailureReason?: string;
	isMoveWithinForcefullySucceeded: boolean;
	/** @format date-time */
	firstPaymentDate?: string;
	isDirectDebitForcefullySucceeded?: boolean;
	moveInContractId?: string;
}

export interface ConfirmMoveOutInResponseOkModel {
	result: ConfirmMoveOutInResponse;
}

export interface ContractAccountDetails {
	/** @format double */
	accountBalance: number;
	/** @format double */
	amountDue: number;
	contractAccountId: string;
	contracts: string[];
	/** @format date-time */
	paymentGeneratedDate: string;
	/** @format double */
	lastBillAmount?: number;
	/** @format date-time */
	lastBillDate?: string;
	/** @format double */
	nextPaymentAmount?: number;
	paymentFrequency?: string;
	paymentMethod: string;
	/** @format date-time */
	paymentDueDate?: string;
	/** @format date-time */
	moveOutDate?: string;
	isMetered: boolean;
	isSewCustomer: boolean;
	paymentScheduleItems: PaymentScheduleItem[];
}

export interface ContractAccountDetailsOkModel {
	result: ContractAccountDetails;
}

export interface ContractAccountMeterDetails {
	contractId: string;
	propertyAddress: AddressResult;
	meters: MeterCheckMeter[];
}

export interface ContractAccountMeterDetailsResponse {
	contracts: ContractMeterDetails[];
}

export interface ContractAccountMeterDetailsResponseOkModel {
	result: ContractAccountMeterDetailsResponse;
}

export interface ContractMeterDetails {
	contractId: string;
	isPropertyActive: boolean;
	meters: MeterDetails[];
}

export interface ContractPropertyDetailsResponse {
	customerServiceFlag: string;
	propertyAddress: AddressResult;
	/** @format date-time */
	moveOutDate?: string;
}

export interface ContractPropertyDetailsResponseOkModel {
	result: ContractPropertyDetailsResponse;
}

export interface CorrespondenceItem {
	/** @format date-time */
	date: string;
	documentType: string;
	viewLink: string;
}

export interface CountryDetails {
	/** @format int64 */
	countryID: number;
	countryName: string;
	isdCode: string;
	countryCode: string;
}

export interface CountryDetailsListOkModel {
	result: CountryDetails[];
}

export interface CreateAnonymousPaymentResponse {
	redirectURL: string;
	paymentPageUrl: string;
	orderCode: string;
}

export interface CreateAnonymousPaymentResponseOkModel {
	result: CreateAnonymousPaymentResponse;
}

export interface CustomerContactInfo {
	telephoneNumber: string;
	mobileTelephoneNumber: string;
}

export interface CustomerCreateAddressInput {
	houseNumber?: string;
	street: string;
	supplementaryLine1?: string;
	supplementaryLine2?: string;
	supplementaryLine3?: string;
	city: string;
	country: string;
	region?: string;
	postcode: string;
}

export interface CustomerInfoForRegistration {
	customerNumber: string;
	lastName: string;
	emailId: string;
}

export interface CustomerInfoForRegistrationOkModel {
	result: CustomerInfoForRegistration;
}

export interface CustomerLinearRegistrationInfo {
	customerBasicInfo: CustomerLinearRegistrationInfoCustomerInfo;
	address: AddressResult;
	customerContactInfo: CustomerContactInfo;
	businessPartnerNumber: string;
	/** @format int32 */
	numberOfOccupants?: number;
}

export interface CustomerLinearRegistrationInfoCustomerInfo {
	firstName: string;
	lastName: string;
	dateOfBirth: string;
}

export interface CustomerLinearRegistrationInfoOkModel {
	result: CustomerLinearRegistrationInfo;
}

export interface DirectDebitDateCalculatorResponse {
	/** @format date-time */
	directDebitDate: string;
}

export interface DirectDebitDateCalculatorResponseOkModel {
	result: DirectDebitDateCalculatorResponse;
}

export interface DirectDebitDetailsInput {
	paymentFrequency: string;
	/** @format int32 */
	paymentDate: number;
	bankDetails: BankDetailsInput;
	/** @format double */
	guideAmount?: number;
	/** @format double */
	directDebitAmount?: number;
	/** @format date-time */
	startDate?: string;
	isMetered: boolean;
}

export interface ErrorRequestResultModel {
	type: string;
	title: string;
	/** @format int32 */
	status: number;
	traceId: string;
	errors: any;
}

export interface FeedbackInput {
	journey: string;
	paymentReference: string;
	/** @format int32 */
	rating: number;
	verbatim: string;
	contactRequired: string;
}

export interface FetchCorrespondenceResponse {
	items: CorrespondenceItem[];
}

export interface FetchCorrespondenceResponseOkModel {
	result: FetchCorrespondenceResponse;
}

export interface FetchCustomerSecurityQuestionsRequestResponse {
	/** @format int64 */
	securityQuestion1Id?: number;
	securityQuestion1Question?: string;
	/** @format int64 */
	securityQuestion2Id?: number;
	securityQuestion2Question?: string;
}

export interface FetchCustomerSecurityQuestionsRequestResponseOkModel {
	result: FetchCustomerSecurityQuestionsRequestResponse;
}

export interface GenerateTokenAndSendEmailInput {
	customerNumber: string;
	newEmailId: string;
	password: string;
}

export interface GetDDAmountResponse {
	/** @format double */
	directDebitAmount?: number;
	isDirectDebitForcefullySucceeded?: boolean;
}

export interface GetDDAmountResponseOkModel {
	result: GetDDAmountResponse;
}

export interface GetMetersResponse {
	contractAccountId: string;
	contracts: ContractAccountMeterDetails[];
}

export interface GetMetersResponseOkModel {
	result: GetMetersResponse;
}

export interface HealthCheckResult {
	status: string;
}

export interface Int32IEnumerableOkModel {
	result: number[];
}

export interface MeterCheckMeter {
	id: string;
	category: string;
	/** @format int32 */
	lastReading: number;
	/** @format date-time */
	lastReadDate: string;
}

export interface MeterDetails {
	id: string;
	category: string;
	/** @format int32 */
	lastReading?: number;
	/** @format date-time */
	lastReadDate?: string;
}

export interface MeterDetailsInput {
	contractAccountNumber: string;
}

export interface MeterReadInput {
	contractId: string;
	meterId: string;
	meterCategory: string;
	/** @format int32 */
	meterReading: number;
	/** @format int32 */
	lastReading: number;
}

export interface ModifyDDDetailsInput {
	contractId: string;
	bankDetails?: BankDetailsInput;
	paymentFrequency: string;
	/** @format int32 */
	paymentDate: number;
	isSimulation: boolean;
	/** @format double */
	guideAmount?: number;
	/** @format double */
	directDebitAmount?: number;
	/** @format date-time */
	startDate?: string;
	isMetered: boolean;
	customerNumber?: string;
}

export interface MoveInDetailsInput {
	title: string;
	firstName: string;
	middleName?: string;
	lastOrgName: string;
	/** @format date-time */
	dateOfBirth: string;
	customerAddressDetails: CustomerCreateAddressInput;
	correspondenceAddress?: CustomerCreateAddressInput;
	mobileNumberCountryCode?: string;
	mobileNumber?: string;
	telephoneNumberCountryCode?: string;
	telephoneNumber?: string;
	emailAddress: string;
	/** @format date-time */
	moveInDate: string;
	occupancyType: string;
	/** @format int32 */
	noPersons: number;
	paymentDetails?: DirectDebitDetailsInput;
	isManualAddress: boolean;
}

export interface MoveInResult {
	customerNumber?: string;
	customerServiceFlag: string;
	productDescription: string;
	contractId: string;
	contractAccountNumber: string;
	paymentReferenceNumber: string;
	directDebitSetupFailureReason?: string;
	isMoveInForcefullySucceeded: boolean;
	isMetered?: boolean;
}

export interface MoveInResultOkModel {
	result: MoveInResult;
}

export interface MoveOutConfirmationInput {
	paymentReference: string;
	/** @format date-time */
	moveOutDate: string;
	meterDetails?: MoveOutMeterReadingInput;
	forwardingAddress: SubmitAddressAnonymousInput;
}

export interface MoveOutCustomerDetailsInput {
	paymentReference: string;
}

export interface MoveOutMeterReadingInput {
	contractId: string;
	meterId: string;
	meterCategory: string;
	/** @format int32 */
	meterReading: number;
}

export interface NoContentResult {
	/** @format int32 */
	statusCode: number;
}

export interface PaymentCheckDetailsInput {
	contractAccountNumber: string;
}

export interface PaymentHistoryDetail {
	/** @format date-time */
	paymentDate: string;
	paymentMethod: string;
	paymentAmount: string;
}

export interface PaymentScheduleItem {
	/** @format date-time */
	paymentDate: string;
	/** @format double */
	paymentAmount: number;
}

export interface PaymentStatusInput {
	/** @format double */
	paymentAmount: number;
	contractAccountNumber: string;
}

export interface PopulateModifyDDInput {
	contractNo: string;
}

export enum PopulatePaymentFrequency {
	M = 'M',
	F = 'F',
	H = 'H',
	A = 'A',
}

export interface PrePopulateCustomerInfoInput {
	activationCode: string;
}

export interface ProblemDetails {
	type?: string;
	title?: string;
	/** @format int32 */
	status?: number;
	detail?: string;
	instance?: string;
}

export enum QuestionsPool {
	DateOfBirth = 'DateOfBirth',
	Mobile = 'Mobile',
	MovedInDate = 'MovedInDate',
	LastBillDate = 'LastBillDate',
	LastBillAmount = 'LastBillAmount',
	NumberOfOccupants = 'NumberOfOccupants',
}

export interface ResendRegistrationLinkInput {
	activationCode: string;
	isDigitalBillingPromo: boolean;
}

export interface SAPModifyDDDetailsResponse {
	/** @format double */
	recommendedAmount?: number;
	/** @format double */
	minAmount?: number;
	/** @format double */
	maxAmount?: number;
	/** @format date-time */
	firstPaymentDate?: string;
}

export interface SAPModifyDDDetailsResponseOkModel {
	result: SAPModifyDDDetailsResponse;
}

export interface SAPPopulateDDDetailsResponse {
	bankDetails: BankDetails;
	paymentFrequency: PopulatePaymentFrequency;
	/** @format int32 */
	paymentDate?: number;
	/** @format double */
	currentAmount?: number;
}

export interface SAPPopulateDDDetailsResponseOkModel {
	result: SAPPopulateDDDetailsResponse;
}

export interface SavePasswordInput {
	activationCode: string;
	password: string;
}

export interface SearchAddressInput {
	pafCode: string;
	isPafSearchRequired: boolean;
}

export interface SearchAddressResponse {
	addresses: AddressResult[];
}

export interface SearchAddressResponseOkModel {
	result: SearchAddressResponse;
}

export interface SecurityDetailInput {
	activationCode: string;
}

export interface SecurityQuestionValidationResponse {
	isCorrect: boolean;
}

export interface SecurityQuestionValidationResponseOkModel {
	result: SecurityQuestionValidationResponse;
}

export interface SetUpDDSubmitResponse {
	/** @format date-time */
	firstPaymentDate?: string;
	isDirectDebitForcefullySucceeded?: boolean;
}

export interface SetUpDDSubmitResponseOkModel {
	result: SetUpDDSubmitResponse;
}

export interface SubmitAddressAnonymousInput {
	houseNumber?: string;
	street: string;
	supplementaryLine1?: string;
	supplementaryLine2?: string;
	supplementaryLine3?: string;
	city: string;
	country: string;
	region?: string;
	postcode: string;
}

export interface SubmitAnonymousMeterReadingInput {
	meterId: string;
	/** @format int32 */
	newReading: number;
	contractId: string;
	/** @format int32 */
	lastReading: number;
	/** @format date-time */
	lastReadingDate: string;
	meterCategory: string;
	/** @format int64 */
	customerNumber: number;
	emailAddress?: string;
}

export interface SubmitDirectDebitDetailsAuthInput {
	contractId: string;
	bankDetails: BankDetailsInput;
	paymentFrequency: string;
	/** @format int32 */
	paymentDate: number;
	/** @format double */
	guideAmount?: number;
	/** @format double */
	directDebitAmount?: number;
	/** @format date-time */
	startDate?: string;
	isMetered: boolean;
	customerNumber?: string;
	isMoveinGhosted?: boolean;
}

export interface SubmitDirectDebitPaymentFrequencyAuthInput {
	paymentFrequency: string;
	/** @format int32 */
	paymentDate: number;
	contractId: string;
	bankDetails: BankDetailsInput;
	/** @format double */
	guideAmount?: number;
	/** @format double */
	directDebitAmount?: number;
	/** @format date-time */
	startDate?: string;
	isMetered: boolean;
	customerNumber?: string;
}

export interface SubmitMeterReadingInput {
	meterId: string;
	/** @format int32 */
	newReading: number;
	contractId: string;
	/** @format int32 */
	lastReading: number;
	/** @format date-time */
	lastReadingDate: string;
	meterCategory: string;
}

export interface SubmitMeterReadingResponse {
	isSubmitMeterReadForcefullySucceeded: boolean;
}

export interface SubmitMeterReadingResponseOkModel {
	result: SubmitMeterReadingResponse;
}

export interface SubmitPaymentInput {
	/** @format double */
	paymentAmount: number;
	contractAccountNumber: string;
}

export interface SubmitRegistrationInput {
	customerNumber: string;
	lastName: string;
	emailId: string;
	password: string;
	paperlessFlag: boolean;
	isDigitalBillingPromo?: boolean;
}

export interface TitleList {
	titleId: string;
	titleName: string;
}

export interface TitleListListOkModel {
	result: TitleList[];
}

export interface UpdateCustomerContactNumberInput {
	mobileNumberCountryCode?: string;
	mobileNumber?: string;
	telephoneNumberCountryCode?: string;
	telephoneNumber?: string;
}

export interface UpdateCustomerCorrespondenceAddressInput {
	contractAccountNumber: string;
	correspondenceAddress: CustomerCreateAddressInput;
}

export interface UpdateProfileLoginEmailInput {
	newEmailId: string;
	password: string;
}

export interface UpdateProfileUnauthInput {
	mobileNumberCountryCode?: string;
	mobileNumber?: string;
	telephoneNumberCountryCode?: string;
	telephoneNumber?: string;
	customerNumber: string;
	/** @format date-time */
	dateOfBirth?: string;
	/** @format int32 */
	numberOfOccupants?: number;
}

export interface UserLoginInput {
	emailId: string;
	password: string;
	uid?: string;
}

export interface UserRegisterInput {
	customerNumber: string;
	lastName: string;
	emailId?: string;
}

export interface ValidateBankDetailsAuthInput {
	bankDetails: BankDetailsInput;
	customerNumber?: string;
}

export interface ValidateSecurityQuestionInput {
	/** @format int64 */
	securityQuestionId: number;
	securityAnswer: string;
	activationCode: string;
}

export interface ValidateSecurityTokenInput {
	activationCode: string;
}

export interface VerifyAccountInput {
	address: CustomerCreateAddressInput;
	lastName?: string;
	emailId?: string;
}

export interface VerifyAccountInputForgotEmail {
	address: CustomerCreateAddressInput;
	lastName?: string;
	businessPartnerNumber?: string;
}

export interface VerifyAccountResponse {
	questionPool: QuestionsPool[];
}

export interface VerifyAccountResponseForgotEmail {
	questionPool: QuestionsPool[];
}

export interface VerifyAccountResponseForgotEmailOkModel {
	result: VerifyAccountResponseForgotEmail;
}

export interface VerifyAccountResponseOkModel {
	result: VerifyAccountResponse;
}

export interface VerifyAnswersInput {
	address: CustomerCreateAddressInput;
	lastName?: string;
	emailId?: string;
	/** @format date-time */
	dateOfBirth?: string;
	mobile?: string;
	/** @format date-time */
	movedInDate?: string;
	/** @format date-time */
	lastBillDate?: string;
	/** @format double */
	lastBillAmount?: number;
	/** @format int32 */
	numberOfOccupants?: number;
}

export interface VerifyAnswersInputForgotEmail {
	address: CustomerCreateAddressInput;
	lastName?: string;
	businessPartnerNumber?: string;
	/** @format date-time */
	dateOfBirth?: string;
	mobile?: string;
	/** @format date-time */
	movedInDate?: string;
	/** @format date-time */
	lastBillDate?: string;
	/** @format double */
	lastBillAmount?: number;
	/** @format int32 */
	numberOfOccupants?: number;
}

export interface VerifyAnswersResponse {
	customerNumber: string;
}

export interface VerifyAnswersResponseForgotEmail {
	emailId: string;
}

export interface VerifyAnswersResponseForgotEmailOkModel {
	result: VerifyAnswersResponseForgotEmail;
}

export interface VerifyAnswersResponseOkModel {
	result: VerifyAnswersResponse;
}

export interface VerifyEmailAddressInput {
	emailId: string;
}

export interface VerifyEmailInput {
	emailAddress: string;
}

export interface VerifyEmailResponse {
	isRegistered: boolean;
}

export interface VerifyEmailResponseOkModel {
	result: VerifyEmailResponse;
}

export interface VerifyTokenInput {
	activationCode: string;
}

export interface ViewAccountStatementInput {
	contractAccountNumber: string;
	/** @format date-time */
	fromDate?: string;
	/** @format date-time */
	toDate?: string;
}

export interface ViewPaymentHistoryInput {
	contractAccountId: string;
	/** @format date-time */
	fromDate?: string;
	/** @format date-time */
	toDate?: string;
}

export interface ViewPaymentHistoryResponse {
	paymentHistory: PaymentHistoryDetail[];
}

export interface ViewPaymentHistoryResponseOkModel {
	result: ViewPaymentHistoryResponse;
}

export interface WaterUsageHistoryItem {
	meterReadType?: string;
	/** @format date-time */
	startReadDate: string;
	/** @format date-time */
	lastReadDate: string;
	/** @format int32 */
	noOfDays: number;
	consumption?: string;
	/** @format int32 */
	dailyAverage?: number;
	/** @format double */
	costPerDay?: number;
}

export interface WaterUsageHistoryResponse {
	items: WaterUsageHistoryItem[];
}

export interface WaterUsageHistoryResponseOkModel {
	result: WaterUsageHistoryResponse;
}

export interface WaterUsageResponse {
	/** @format double */
	costPerDay?: number;
	/** @format int32 */
	dailyWaterAverage?: number;
}

export interface WaterUsageResponseOkModel {
	result: WaterUsageResponse;
}

export type QueryParamsType = Record<string | number, any>;
export type ResponseFormat = keyof Omit<Body, 'body' | 'bodyUsed'>;

export interface FullRequestParams extends Omit<RequestInit, 'body'> {
	/** set parameter to `true` for call `securityWorker` for this request */
	secure?: boolean;
	/** request path */
	path: string;
	/** content type of request body */
	type?: ContentType;
	/** query params */
	query?: QueryParamsType;
	/** format of response (i.e. response.json() -> format: "json") */
	format?: ResponseFormat;
	/** request body */
	body?: unknown;
	/** base url */
	baseUrl?: string;
	/** request cancellation token */
	cancelToken?: CancelToken;
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>;

export interface ApiConfig<SecurityDataType = unknown> {
	baseUrl?: string;
	baseApiParams?: Omit<RequestParams, 'baseUrl' | 'cancelToken' | 'signal'>;
	securityWorker?: (securityData: SecurityDataType | null) => Promise<RequestParams | void> | RequestParams | void;
	customFetch?: typeof fetch;
}

export interface HttpResponse<D extends unknown, E extends unknown = unknown> extends Response {
	data: D;
	error: E;
}

type CancelToken = Symbol | string | number;

export enum ContentType {
	Json = 'application/json',
	FormData = 'multipart/form-data',
	UrlEncoded = 'application/x-www-form-urlencoded',
}

export class HttpClient<SecurityDataType = unknown> {
	public baseUrl: string = '';
	private securityData: SecurityDataType | null = null;
	private securityWorker?: ApiConfig<SecurityDataType>['securityWorker'];
	private abortControllers = new Map<CancelToken, AbortController>();
	private customFetch = (...fetchParams: Parameters<typeof fetch>) => fetch(...fetchParams);

	private baseApiParams: RequestParams = {
		credentials: 'same-origin',
		headers: {},
		redirect: 'follow',
		referrerPolicy: 'no-referrer',
	};

	constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
		Object.assign(this, apiConfig);
	}

	public setSecurityData = (data: SecurityDataType | null) => {
		this.securityData = data;
	};

	protected encodeQueryParam(key: string, value: any) {
		const encodedKey = encodeURIComponent(key);
		return `${encodedKey}=${encodeURIComponent(typeof value === 'number' ? value : `${value}`)}`;
	}

	protected addQueryParam(query: QueryParamsType, key: string) {
		return this.encodeQueryParam(key, query[key]);
	}

	protected addArrayQueryParam(query: QueryParamsType, key: string) {
		const value = query[key];
		return value.map((v: any) => this.encodeQueryParam(key, v)).join('&');
	}

	protected toQueryString(rawQuery?: QueryParamsType): string {
		const query = rawQuery || {};
		const keys = Object.keys(query).filter((key) => 'undefined' !== typeof query[key]);
		return keys
			.map((key) => (Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key)))
			.join('&');
	}

	protected addQueryParams(rawQuery?: QueryParamsType): string {
		const queryString = this.toQueryString(rawQuery);
		return queryString ? `?${queryString}` : '';
	}

	private contentFormatters: Record<ContentType, (input: any) => any> = {
		[ContentType.Json]: (input: any) =>
			input !== null && (typeof input === 'object' || typeof input === 'string') ? JSON.stringify(input) : input,
		[ContentType.FormData]: (input: any) =>
			Object.keys(input || {}).reduce((formData, key) => {
				const property = input[key];
				formData.append(
					key,
					property instanceof Blob
						? property
						: typeof property === 'object' && property !== null
						? JSON.stringify(property)
						: `${property}`,
				);
				return formData;
			}, new FormData()),
		[ContentType.UrlEncoded]: (input: any) => this.toQueryString(input),
	};

	protected mergeRequestParams(params1: RequestParams, params2?: RequestParams): RequestParams {
		return {
			...this.baseApiParams,
			...params1,
			...(params2 || {}),
			headers: {
				...(this.baseApiParams.headers || {}),
				...(params1.headers || {}),
				...((params2 && params2.headers) || {}),
			},
		};
	}

	protected createAbortSignal = (cancelToken: CancelToken): AbortSignal | undefined => {
		if (this.abortControllers.has(cancelToken)) {
			const abortController = this.abortControllers.get(cancelToken);
			if (abortController) {
				return abortController.signal;
			}
			return void 0;
		}

		const abortController = new AbortController();
		this.abortControllers.set(cancelToken, abortController);
		return abortController.signal;
	};

	public abortRequest = (cancelToken: CancelToken) => {
		const abortController = this.abortControllers.get(cancelToken);

		if (abortController) {
			abortController.abort();
			this.abortControllers.delete(cancelToken);
		}
	};

	public request = async <T = any, E = any>({
		body,
		secure,
		path,
		type,
		query,
		format,
		baseUrl,
		cancelToken,
		...params
	}: FullRequestParams): Promise<T> => {
		const secureParams =
			((typeof secure === 'boolean' ? secure : this.baseApiParams.secure) &&
				this.securityWorker &&
				(await this.securityWorker(this.securityData))) ||
			{};
		const requestParams = this.mergeRequestParams(params, secureParams);
		const queryString = query && this.toQueryString(query);
		const payloadFormatter = this.contentFormatters[type || ContentType.Json];
		const responseFormat = format || requestParams.format;

		return this.customFetch(`${baseUrl || this.baseUrl || ''}${path}${queryString ? `?${queryString}` : ''}`, {
			...requestParams,
			headers: {
				...(requestParams.headers || {}),
				...(type && type !== ContentType.FormData ? { 'Content-Type': type } : {}),
			},
			signal: cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal,
			body: typeof body === 'undefined' || body === null ? null : payloadFormatter(body),
		}).then(async (response) => {
			const r = response as HttpResponse<T, E>;
			r.data = null as unknown as T;
			r.error = null as unknown as E;

			const data = !responseFormat
				? r
				: await response[responseFormat]()
						.then((data) => {
							if (r.ok) {
								r.data = data;
							} else {
								r.error = data;
							}
							return r;
						})
						.catch((e) => {
							r.error = e;
							return r;
						});

			if (cancelToken) {
				this.abortControllers.delete(cancelToken);
			}

			if (!response.ok) throw data;
			return data.data;
		});
	};
}

/**
 * @title EServices API
 * @version v1
 */
export class Api<SecurityDataType extends unknown> extends HttpClient<SecurityDataType> {
	api = {
		/**
		 * No description
		 *
		 * @tags AccountStatement
		 * @name AccountStatementViewAccountStatementCreate
		 * @request POST:/api/account-statement/view-account-statement
		 * @secure
		 * @response `200` `AccountStatementResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		accountStatementViewAccountStatementCreate: (data: ViewAccountStatementInput, params: RequestParams = {}) =>
			this.request<AccountStatementResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/account-statement/view-account-statement`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags AccountStatement
		 * @name AccountStatementViewPaymentHistoryCreate
		 * @request POST:/api/account-statement/view-payment-history
		 * @secure
		 * @response `200` `ViewPaymentHistoryResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		accountStatementViewPaymentHistoryCreate: (data: ViewPaymentHistoryInput, params: RequestParams = {}) =>
			this.request<ViewPaymentHistoryResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/account-statement/view-payment-history`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Correspondence
		 * @name CorrespondenceGetList
		 * @request GET:/api/correspondence/get
		 * @secure
		 * @response `200` `FetchCorrespondenceResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		correspondenceGetList: (
			query?: {
				ContractAccountNumber?: string;
				/** @format date-time */
				From?: string;
				/** @format date-time */
				To?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<FetchCorrespondenceResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/correspondence/get`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Correspondence
		 * @name CorrespondenceGetPdfBillList
		 * @request GET:/api/correspondence/get-pdf-bill
		 * @secure
		 * @response `200` `void` Success
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 */
		correspondenceGetPdfBillList: (
			query?: {
				ContractAccountNumber?: string;
				DocumentId?: string;
				/** @format date-time */
				Date?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, void>({
				path: `/api/correspondence/get-pdf-bill`,
				method: 'GET',
				query: query,
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Correspondence
		 * @name CorrespondenceGetPdfCorrespondenceList
		 * @request GET:/api/correspondence/get-pdf-correspondence
		 * @secure
		 * @response `200` `void` Success
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 */
		correspondenceGetPdfCorrespondenceList: (
			query?: {
				ContractAccountNumber?: string;
				DocumentId?: string;
				/** @format date-time */
				Date?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, void>({
				path: `/api/correspondence/get-pdf-correspondence`,
				method: 'GET',
				query: query,
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsBasicDetailsList
		 * @request GET:/api/customer-details/basic-details
		 * @response `200` `AuthenticatedCustomerOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsBasicDetailsList: (params: RequestParams = {}) =>
			this.request<AuthenticatedCustomerOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/basic-details`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsContractAccountDetailsList
		 * @request GET:/api/customer-details/contract-account-details
		 * @response `200` `ContractAccountDetailsOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsContractAccountDetailsList: (
			query?: {
				ContractAccountNumber?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<ContractAccountDetailsOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/contract-account-details`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsContractPropertyDetailsList
		 * @request GET:/api/customer-details/contract-property-details
		 * @response `200` `ContractPropertyDetailsResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsContractPropertyDetailsList: (
			query?: {
				ContractAccountNumber?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<ContractPropertyDetailsResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/contract-property-details`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsContractAccountMeterDetailsList
		 * @request GET:/api/customer-details/contract-account-meter-details
		 * @response `200` `ContractAccountMeterDetailsResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsContractAccountMeterDetailsList: (
			query?: {
				ContractAccountNumber?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<ContractAccountMeterDetailsResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/contract-account-meter-details`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsOptPaperlessBillList
		 * @request GET:/api/customer-details/opt-paperless-bill
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsOptPaperlessBillList: (
			query?: {
				IsPaperlessBilling?: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/opt-paperless-bill`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsCustomerProfileDetailsList
		 * @request GET:/api/customer-details/customer-profile-details
		 * @response `200` `AuthenticatedCustomerProfileDetailsOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsCustomerProfileDetailsList: (params: RequestParams = {}) =>
			this.request<AuthenticatedCustomerProfileDetailsOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/customer-profile-details`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags CustomerDetails
		 * @name CustomerDetailsCustomerPropertyDetailsList
		 * @request GET:/api/customer-details/customer-property-details
		 * @response `200` `AuthenticatedCustomerPropertiesDetailsListOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		customerDetailsCustomerPropertyDetailsList: (params: RequestParams = {}) =>
			this.request<AuthenticatedCustomerPropertiesDetailsListOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/customer-details/customer-property-details`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags DirectDebit
		 * @name DirectDebitDirectDebitConfirmationDetailsAuthCreate
		 * @request POST:/api/direct-debit/direct-debit-confirmation-details-auth
		 * @response `200` `SetUpDDSubmitResponseOkModel` Success
		 */
		directDebitDirectDebitConfirmationDetailsAuthCreate: (
			data: SubmitDirectDebitDetailsAuthInput,
			params: RequestParams = {},
		) =>
			this.request<SetUpDDSubmitResponseOkModel, any>({
				path: `/api/direct-debit/direct-debit-confirmation-details-auth`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags DirectDebit
		 * @name DirectDebitDirectDebitBankdetailsAuthCreate
		 * @request POST:/api/direct-debit/direct-debit-bankdetails-auth
		 * @response `204` `NoContentResult` Success
		 */
		directDebitDirectDebitBankdetailsAuthCreate: (data: ValidateBankDetailsAuthInput, params: RequestParams = {}) =>
			this.request<NoContentResult, any>({
				path: `/api/direct-debit/direct-debit-bankdetails-auth`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags DirectDebit
		 * @name DirectDebitDirectDebitPaymentFrequencyAuthCreate
		 * @request POST:/api/direct-debit/direct-debit-payment-frequency-auth
		 * @response `200` `GetDDAmountResponseOkModel` Success
		 */
		directDebitDirectDebitPaymentFrequencyAuthCreate: (
			data: SubmitDirectDebitPaymentFrequencyAuthInput,
			params: RequestParams = {},
		) =>
			this.request<GetDDAmountResponseOkModel, any>({
				path: `/api/direct-debit/direct-debit-payment-frequency-auth`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags DirectDebit
		 * @name DirectDebitPopulateDirectDebitDetailsCreate
		 * @request POST:/api/direct-debit/populate-direct-debit-details
		 * @secure
		 * @response `200` `SAPPopulateDDDetailsResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		directDebitPopulateDirectDebitDetailsCreate: (data: PopulateModifyDDInput, params: RequestParams = {}) =>
			this.request<SAPPopulateDDDetailsResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/direct-debit/populate-direct-debit-details`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags DirectDebit
		 * @name DirectDebitModifyDirectDebitDetailsCreate
		 * @request POST:/api/direct-debit/modify-direct-debit-details
		 * @response `200` `SAPModifyDDDetailsResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		directDebitModifyDirectDebitDetailsCreate: (data: ModifyDDDetailsInput, params: RequestParams = {}) =>
			this.request<SAPModifyDDDetailsResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/direct-debit/modify-direct-debit-details`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags DirectDebit
		 * @name DirectDebitDirectDebitDateCalculatorList
		 * @request GET:/api/direct-debit/direct-debit-date-calculator
		 * @response `200` `DirectDebitDateCalculatorResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		directDebitDirectDebitDateCalculatorList: (
			query?: {
				/** @format date-time */
				date?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<DirectDebitDateCalculatorResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/direct-debit/direct-debit-date-calculator`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Feedback
		 * @name FeedbackLoggingFeedbackCreate
		 * @request POST:/api/feedback/logging-feedback
		 * @secure
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		feedbackLoggingFeedbackCreate: (data: FeedbackInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/feedback/logging-feedback`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotBusinessPartnerNumber
		 * @name ForgotBusinessPartnerNumberVerifyAccountCreate
		 * @request POST:/api/forgot-business-partner-number/verify-account
		 * @response `200` `VerifyAccountResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotBusinessPartnerNumberVerifyAccountCreate: (data: VerifyAccountInput, params: RequestParams = {}) =>
			this.request<VerifyAccountResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-business-partner-number/verify-account`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotBusinessPartnerNumber
		 * @name ForgotBusinessPartnerNumberVerifyAnswersCreate
		 * @request POST:/api/forgot-business-partner-number/verify-answers
		 * @response `200` `VerifyAnswersResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotBusinessPartnerNumberVerifyAnswersCreate: (data: VerifyAnswersInput, params: RequestParams = {}) =>
			this.request<VerifyAnswersResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-business-partner-number/verify-answers`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotEmail
		 * @name ForgotEmailVerifyAccountCreate
		 * @request POST:/api/forgot-email/verify-account
		 * @response `200` `VerifyAccountResponseForgotEmailOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotEmailVerifyAccountCreate: (data: VerifyAccountInputForgotEmail, params: RequestParams = {}) =>
			this.request<VerifyAccountResponseForgotEmailOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-email/verify-account`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotEmail
		 * @name ForgotEmailVerifyAnswersCreate
		 * @request POST:/api/forgot-email/verify-answers
		 * @response `200` `VerifyAnswersResponseForgotEmailOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotEmailVerifyAnswersCreate: (data: VerifyAnswersInputForgotEmail, params: RequestParams = {}) =>
			this.request<VerifyAnswersResponseForgotEmailOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-email/verify-answers`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotPassword
		 * @name ForgotPasswordVerifyEmailAddressCreate
		 * @request POST:/api/forgot-password/verify-email-address
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotPasswordVerifyEmailAddressCreate: (data: VerifyEmailAddressInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-password/verify-email-address`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotPassword
		 * @name ForgotPasswordPopulateSecurityDetailsCreate
		 * @request POST:/api/forgot-password/populate-security-details
		 * @response `200` `FetchCustomerSecurityQuestionsRequestResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotPasswordPopulateSecurityDetailsCreate: (data: SecurityDetailInput, params: RequestParams = {}) =>
			this.request<
				FetchCustomerSecurityQuestionsRequestResponseOkModel,
				BadRequestResultModel | ErrorRequestResultModel
			>({
				path: `/api/forgot-password/populate-security-details`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotPassword
		 * @name ForgotPasswordValidateSecurityQuestionCreate
		 * @request POST:/api/forgot-password/validate-security-question
		 * @response `204` `SecurityQuestionValidationResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotPasswordValidateSecurityQuestionCreate: (data: ValidateSecurityQuestionInput, params: RequestParams = {}) =>
			this.request<SecurityQuestionValidationResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-password/validate-security-question`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotPassword
		 * @name ForgotPasswordValidateSecurityTokenCreate
		 * @request POST:/api/forgot-password/validate-security-token
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotPasswordValidateSecurityTokenCreate: (data: ValidateSecurityTokenInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-password/validate-security-token`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags ForgotPassword
		 * @name ForgotPasswordSavePasswordCreate
		 * @request POST:/api/forgot-password/save-password
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		forgotPasswordSavePasswordCreate: (data: SavePasswordInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/forgot-password/save-password`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Location
		 * @name SearchAddressCreate
		 * @request POST:/api/searchAddress
		 * @response `200` `SearchAddressResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		searchAddressCreate: (data: SearchAddressInput, params: RequestParams = {}) =>
			this.request<SearchAddressResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/searchAddress`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Location
		 * @name CountryListList
		 * @request GET:/api/countryList
		 * @response `200` `CountryDetailsListOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		countryListList: (params: RequestParams = {}) =>
			this.request<CountryDetailsListOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/countryList`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginCustomerLoginCreate
		 * @request POST:/api/login/customer-login
		 * @response `200` `AuthenticatedTokenModelOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		loginCustomerLoginCreate: (data: UserLoginInput, params: RequestParams = {}) =>
			this.request<AuthenticatedTokenModelOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/login/customer-login`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginTRefreshCreate
		 * @request POST:/api/login/t/refresh
		 * @response `200` `AuthenticatedTokenModelOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		loginTRefreshCreate: (params: RequestParams = {}) =>
			this.request<AuthenticatedTokenModelOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/login/t/refresh`,
				method: 'POST',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginRefreshCreate
		 * @request POST:/api/login/refresh
		 * @response `200` `AuthenticatedTokenModelOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		loginRefreshCreate: (params: RequestParams = {}) =>
			this.request<AuthenticatedTokenModelOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/login/refresh`,
				method: 'POST',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginTRevokeCreate
		 * @request POST:/api/login/t/revoke
		 * @response `200` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		loginTRevokeCreate: (params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/login/t/revoke`,
				method: 'POST',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginRevokeCreate
		 * @request POST:/api/login/revoke
		 * @response `200` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		loginRevokeCreate: (params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/login/revoke`,
				method: 'POST',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginTimeRemainingList
		 * @request GET:/api/login/time-remaining
		 * @secure
		 * @response `200` `AuthenticatedTokenModelOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		loginTimeRemainingList: (params: RequestParams = {}) =>
			this.request<AuthenticatedTokenModelOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/login/time-remaining`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Login
		 * @name LoginLoginPathList
		 * @request GET:/api/login/login-path
		 * @response `200` `void` Success
		 */
		loginLoginPathList: (params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/login/login-path`,
				method: 'GET',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Meters
		 * @name MetersSubmitMeterReadingCreate
		 * @request POST:/api/meters/submit-meter-reading
		 * @secure
		 * @response `200` `SubmitMeterReadingResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		metersSubmitMeterReadingCreate: (data: SubmitMeterReadingInput, params: RequestParams = {}) =>
			this.request<SubmitMeterReadingResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/meters/submit-meter-reading`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Meters
		 * @name MetersSubmitAnonymousMeterReadingCreate
		 * @request POST:/api/meters/submit-anonymous-meter-reading
		 * @response `200` `SubmitMeterReadingResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		metersSubmitAnonymousMeterReadingCreate: (data: SubmitAnonymousMeterReadingInput, params: RequestParams = {}) =>
			this.request<SubmitMeterReadingResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/meters/submit-anonymous-meter-reading`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Meters
		 * @name MetersGetMeterDetailsV2Create
		 * @request POST:/api/meters/get-meter-details-v2
		 * @secure
		 * @response `200` `GetMetersResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		metersGetMeterDetailsV2Create: (data: MeterDetailsInput, params: RequestParams = {}) =>
			this.request<GetMetersResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/meters/get-meter-details-v2`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Meters
		 * @name MetersGetAnonymousMeterDetailsV2Create
		 * @request POST:/api/meters/get-anonymous-meter-details-v2
		 * @response `200` `GetMetersResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		metersGetAnonymousMeterDetailsV2Create: (data: AnonymousMeterDetailsInput, params: RequestParams = {}) =>
			this.request<GetMetersResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/meters/get-anonymous-meter-details-v2`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveIn
		 * @name MoveInGetTitlesList
		 * @request GET:/api/move-in/get-titles
		 * @response `200` `TitleListListOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveInGetTitlesList: (params: RequestParams = {}) =>
			this.request<TitleListListOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/move-in/get-titles`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveIn
		 * @name MoveInRequestMoveInCreate
		 * @request POST:/api/move-in/request-move-in
		 * @response `200` `MoveInResultOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveInRequestMoveInCreate: (data: MoveInDetailsInput, params: RequestParams = {}) =>
			this.request<MoveInResultOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/move-in/request-move-in`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveIn
		 * @name MoveInVerifyEmailCreate
		 * @request POST:/api/move-in/verify-email
		 * @response `200` `VerifyEmailResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveInVerifyEmailCreate: (data: VerifyEmailInput, params: RequestParams = {}) =>
			this.request<VerifyEmailResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/move-in/verify-email`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveOut
		 * @name MoveOutAnonymousCustomerDetailsCreate
		 * @request POST:/api/move-out/anonymous-customer-details
		 * @response `200` `CloseAccountAnonymousUserDetailsOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveOutAnonymousCustomerDetailsCreate: (data: AnonymousMoveOutCustomerDetailsInput, params: RequestParams = {}) =>
			this.request<CloseAccountAnonymousUserDetailsOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/move-out/anonymous-customer-details`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveOut
		 * @name MoveOutCustomerDetailsCreate
		 * @request POST:/api/move-out/customer-details
		 * @secure
		 * @response `200` `CloseAccountAnonymousUserDetailsOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveOutCustomerDetailsCreate: (data: MoveOutCustomerDetailsInput, params: RequestParams = {}) =>
			this.request<CloseAccountAnonymousUserDetailsOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/move-out/customer-details`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveOut
		 * @name MoveOutAnonymousConfirmMoveOutCreate
		 * @request POST:/api/move-out/anonymous-confirm-move-out
		 * @response `200` `ConfirmMoveoutAnonymousResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveOutAnonymousConfirmMoveOutCreate: (data: AnonymousMoveOutConfirmationInput, params: RequestParams = {}) =>
			this.request<ConfirmMoveoutAnonymousResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/move-out/anonymous-confirm-move-out`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveOut
		 * @name MoveOutConfirmMoveOutCreate
		 * @request POST:/api/move-out/confirm-move-out
		 * @secure
		 * @response `200` `ConfirmMoveoutAnonymousResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveOutConfirmMoveOutCreate: (data: MoveOutConfirmationInput, params: RequestParams = {}) =>
			this.request<ConfirmMoveoutAnonymousResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/move-out/confirm-move-out`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags MoveWithin
		 * @name MoveWithinRequestMoveWithinCreate
		 * @request POST:/api/move-within/request-move-within
		 * @secure
		 * @response `200` `ConfirmMoveOutInResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		moveWithinRequestMoveWithinCreate: (data: ConfirmMoveOutInInput, params: RequestParams = {}) =>
			this.request<ConfirmMoveOutInResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/move-within/request-move-within`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Notification
		 * @name NotificationGetUserNotificationList
		 * @request GET:/api/notification/get-user-notification
		 * @secure
		 * @response `200` `Int32IEnumerableOkModel` Success
		 * @response `401` `ProblemDetails` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `404` `ProblemDetails` Not Found
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		notificationGetUserNotificationList: (
			query?: {
				IsActiveCustomer?: boolean;
				IsCashCustomer?: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<Int32IEnumerableOkModel, ProblemDetails | void | ErrorRequestResultModel>({
				path: `/api/notification/get-user-notification`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Notification
		 * @name NotificationSetUserNotificationNotificationIdCreate
		 * @request POST:/api/notification/set-user-notification/notificationId
		 * @secure
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `ProblemDetails` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		notificationSetUserNotificationNotificationIdCreate: (
			query?: {
				/** @format int32 */
				notificationId?: number;
			},
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | ProblemDetails | void | ErrorRequestResultModel>({
				path: `/api/notification/set-user-notification/notificationId`,
				method: 'POST',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillAnonymousCheckDetailsCreate
		 * @request POST:/api/pay-bill/anonymous-check-details
		 * @response `200` `AnonymousCheckResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillAnonymousCheckDetailsCreate: (data: AnonymousPaymentCheckDetailsInput, params: RequestParams = {}) =>
			this.request<AnonymousCheckResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/pay-bill/anonymous-check-details`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillCheckDetailsCreate
		 * @request POST:/api/pay-bill/check-details
		 * @secure
		 * @response `200` `AnonymousCheckResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillCheckDetailsCreate: (data: PaymentCheckDetailsInput, params: RequestParams = {}) =>
			this.request<AnonymousCheckResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/pay-bill/check-details`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillSubmitAnonymousPaymentCreate
		 * @request POST:/api/pay-bill/submit-anonymous-payment
		 * @response `200` `CreateAnonymousPaymentResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillSubmitAnonymousPaymentCreate: (data: AnonymousPaymentInput, params: RequestParams = {}) =>
			this.request<CreateAnonymousPaymentResponseOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/pay-bill/submit-anonymous-payment`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillSubmitPaymentCreate
		 * @request POST:/api/pay-bill/submit-payment
		 * @secure
		 * @response `200` `CreateAnonymousPaymentResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillSubmitPaymentCreate: (data: SubmitPaymentInput, params: RequestParams = {}) =>
			this.request<CreateAnonymousPaymentResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/pay-bill/submit-payment`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillAnonymousPaymentFailureCreate
		 * @request POST:/api/pay-bill/anonymous-payment-failure
		 * @response `204` `void` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillAnonymousPaymentFailureCreate: (data: AnonymousPaymentStatusInput, params: RequestParams = {}) =>
			this.request<void, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/pay-bill/anonymous-payment-failure`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillPaymentFailureCreate
		 * @request POST:/api/pay-bill/payment-failure
		 * @secure
		 * @response `204` `void` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillPaymentFailureCreate: (data: PaymentStatusInput, params: RequestParams = {}) =>
			this.request<void, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/pay-bill/payment-failure`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillAnonymousPaymentSuccessCreate
		 * @request POST:/api/pay-bill/anonymous-payment-success
		 * @response `204` `void` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillAnonymousPaymentSuccessCreate: (data: AnonymousPaymentStatusInput, params: RequestParams = {}) =>
			this.request<void, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/pay-bill/anonymous-payment-success`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags PayBill
		 * @name PayBillPaymentSuccessCreate
		 * @request POST:/api/pay-bill/payment-success
		 * @secure
		 * @response `204` `void` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		payBillPaymentSuccessCreate: (data: PaymentStatusInput, params: RequestParams = {}) =>
			this.request<void, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/pay-bill/payment-success`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags RegisterAccount
		 * @name RegisterAccountUserRegistrationForLinearRegistrationCreate
		 * @request POST:/api/register-account/user-registration-for-linear-registration
		 * @response `200` `CustomerLinearRegistrationInfoOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		registerAccountUserRegistrationForLinearRegistrationCreate: (data: UserRegisterInput, params: RequestParams = {}) =>
			this.request<CustomerLinearRegistrationInfoOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/register-account/user-registration-for-linear-registration`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags RegisterAccount
		 * @name RegisterAccountSubmitRegistrationForLinearRegistrationCreate
		 * @request POST:/api/register-account/submit-registration-for-linear-registration
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		registerAccountSubmitRegistrationForLinearRegistrationCreate: (
			data: SubmitRegistrationInput,
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/register-account/submit-registration-for-linear-registration`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags RegisterAccount
		 * @name RegisterAccountActivateAccountCreate
		 * @request POST:/api/register-account/activate-account
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		registerAccountActivateAccountCreate: (data: ActivateAccountInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/register-account/activate-account`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags RegisterAccount
		 * @name RegisterAccountCustomerInfoForPrePopulateCreate
		 * @request POST:/api/register-account/customer-info-for-pre-populate
		 * @response `200` `CustomerInfoForRegistrationOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		registerAccountCustomerInfoForPrePopulateCreate: (data: PrePopulateCustomerInfoInput, params: RequestParams = {}) =>
			this.request<CustomerInfoForRegistrationOkModel, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/register-account/customer-info-for-pre-populate`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags RegisterAccount
		 * @name RegisterAccountResendRegistrationLinkCreate
		 * @request POST:/api/register-account/resend-registration-link
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		registerAccountResendRegistrationLinkCreate: (data: ResendRegistrationLinkInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/register-account/resend-registration-link`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileUpdateProfileContactNumberCreate
		 * @request POST:/api/update-customer-profile/update-profile-contact-number
		 * @secure
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileUpdateProfileContactNumberCreate: (
			data: UpdateCustomerContactNumberInput,
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/update-profile-contact-number`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileUpdateProfileUnauthCreate
		 * @request POST:/api/update-customer-profile/update-profile-unauth
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileUpdateProfileUnauthCreate: (data: UpdateProfileUnauthInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/update-profile-unauth`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileGenerateTokenAndSendEmailCreate
		 * @request POST:/api/update-customer-profile/generate-token-and-send-email
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileGenerateTokenAndSendEmailCreate: (
			data: GenerateTokenAndSendEmailInput,
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/generate-token-and-send-email`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileVerifyTokenAndUpdateEmailCreate
		 * @request POST:/api/update-customer-profile/verify-token-and-update-email
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileVerifyTokenAndUpdateEmailCreate: (data: VerifyTokenInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/verify-token-and-update-email`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileUpdateProfileLoginEmailCreate
		 * @request POST:/api/update-customer-profile/update-profile-login-email
		 * @secure
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileUpdateProfileLoginEmailCreate: (
			data: UpdateProfileLoginEmailInput,
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/update-profile-login-email`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileChangePasswordCreate
		 * @request POST:/api/update-customer-profile/change-password
		 * @secure
		 * @response `204` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileChangePasswordCreate: (data: ChangePasswordInput, params: RequestParams = {}) =>
			this.request<NoContentResult, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/change-password`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfilePopulateSecurityQuestionsList
		 * @request GET:/api/update-customer-profile/populate-security-questions
		 * @secure
		 * @response `200` `FetchCustomerSecurityQuestionsRequestResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfilePopulateSecurityQuestionsList: (params: RequestParams = {}) =>
			this.request<
				FetchCustomerSecurityQuestionsRequestResponseOkModel,
				BadRequestResultModel | void | ErrorRequestResultModel
			>({
				path: `/api/update-customer-profile/populate-security-questions`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags UpdateCustomerProfile
		 * @name UpdateCustomerProfileUpdateCorrespondenceAddressCreate
		 * @request POST:/api/update-customer-profile/update-correspondence-address
		 * @secure
		 * @response `200` `NoContentResult` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		updateCustomerProfileUpdateCorrespondenceAddressCreate: (
			data: UpdateCustomerCorrespondenceAddressInput,
			params: RequestParams = {},
		) =>
			this.request<NoContentResult, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/update-customer-profile/update-correspondence-address`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags WaterUsage
		 * @name WaterUsageViewWaterUsageList
		 * @request GET:/api/water-usage/view-water-usage
		 * @secure
		 * @response `200` `WaterUsageResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		waterUsageViewWaterUsageList: (
			query?: {
				ContractAccountNumber?: string;
				ContractId?: string;
				MeterCategory?: string;
				MeterId?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<WaterUsageResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/water-usage/view-water-usage`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags WaterUsage
		 * @name WaterUsageViewWaterUsageHistoryList
		 * @request GET:/api/water-usage/view-water-usage-history
		 * @secure
		 * @response `200` `WaterUsageHistoryResponseOkModel` Success
		 * @response `400` `BadRequestResultModel` Bad Request
		 * @response `401` `void` Unauthorized
		 * @response `403` `void` Forbidden
		 * @response `500` `ErrorRequestResultModel` Server Error
		 */
		waterUsageViewWaterUsageHistoryList: (
			query?: {
				ContractAccountNumber?: string;
				ContractId?: string;
				MeterCategory?: string;
				MeterId?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<WaterUsageHistoryResponseOkModel, BadRequestResultModel | void | ErrorRequestResultModel>({
				path: `/api/water-usage/view-water-usage-history`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),
	};
}
